import { useState } from 'react';
import { api, ApiError, downloadBlob } from '../lib/api';

// Export buttons for a list page: CSV or Excel with the list's current filters. Up to 5,000 rows download at once;
// a larger list is built in the background and fetched through a short-lived link when it is ready.

type Started = { export_id: string };
type Built = { status: string; download_url: string | null; error: string | null; row_count: number | null };
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** `base` is the list's API path (/v1/companies, /v1/contacts, /v1/campaigns/{id}/leads); `filters` the list's own. */
export function ExportButtons({ base, filters = {}, what }: { base: string; filters?: Record<string, string | undefined>; what: string }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== ''));
  const qs = new URLSearchParams(clean as Record<string, string>).toString();
  const run = async (format: 'csv' | 'xlsx') => {
    setErr(null); setBusy(`Preparing the ${format === 'csv' ? 'CSV' : 'Excel file'}…`);
    try {
      try {
        await downloadBlob(`${base}/export.${format}${qs ? `?${qs}` : ''}`, `${what}.${format}`);
      } catch (e) {
        if (!(e instanceof ApiError && e.code === 'FILE_TOO_LARGE')) throw e;
        // too many rows to download at once: built in the background, then fetched from its signed link
        setBusy('A large export: building it in the background…');
        const s = await api<Started>(`${base}/exports`, { method: 'POST', body: { format, filters: clean } });
        const status = `${base}/exports/${s.export_id}`;
        for (let i = 0; i < 120; i++) {
          await sleep(i < 5 ? 1000 : 3000);
          const b = await api<Built>(status);
          if (b.status === 'completed' && b.download_url) { window.location.assign(b.download_url); return; }
          if (b.status === 'failed') throw new Error(b.error ?? 'The export failed');
        }
        throw new Error('The export is still being built; try again in a few minutes');
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally { setBusy(null); }
  };
  return (
    <span className="actions">
      <button type="button" className="btn" disabled={Boolean(busy)} onClick={() => void run('csv')}>Export CSV</button>
      <button type="button" className="btn" disabled={Boolean(busy)} onClick={() => void run('xlsx')}>Excel</button>
      {busy ? <span className="muted small" role="status">{busy}</span> : null}
      {err ? <span className="error small" role="alert">{err}</span> : null}
    </span>
  );
}
